/**
 * Report-the-question UI (M11 follow-up): generates a plain-text report with
 * the question, explanation, both answers and reproduction ids, plus the
 * user's comment. Reports stay on the user's device (clipboard/download) —
 * there is no report endpoint; the footer points at the GitHub issue tracker.
 */
import { useState } from 'react'
import { buildQuestionReport, ISSUE_URL, type QuestionReportInput } from './questionReport.ts'

export function ReportPanel({
  input,
}: {
  input: Omit<QuestionReportInput, 'comment' | 'reportedAt' | 'appVersion' | 'userAgent'>
}) {
  const [open, setOpen] = useState(false)
  const [comment, setComment] = useState('')
  const [copied, setCopied] = useState(false)

  const build = () =>
    buildQuestionReport({
      ...input,
      comment,
      appVersion: __APP_VERSION__,
      userAgent: navigator.userAgent,
    })

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(build())
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  const download = () => {
    const blob = new Blob([build()], { type: 'text/markdown;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `dmat-report-${input.question.subtest}-seed${input.question.seed}.md`
    link.click()
    URL.revokeObjectURL(url)
  }

  if (!open) {
    return (
      <button type="button" className="drill-exit" onClick={() => setOpen(true)}>
        Report a problem with this question
      </button>
    )
  }

  return (
    <div className="report-panel">
      <p className="muted">
        The report contains the question, its explanation, both answers and reproduction ids
        (subtest, difficulty, seed) — no personal data. Add what you think is wrong, then copy it
        into a <a href={ISSUE_URL} target="_blank" rel="noreferrer">GitHub issue</a> or download it.
      </p>
      <textarea
        className="report-comment"
        rows={4}
        placeholder="What do you think is wrong with this question or its explanation?"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
      />
      <div className="exam-results-actions">
        <button type="button" className="drill-exit" onClick={() => void copy()}>
          {copied ? 'Copied ✓' : 'Copy report'}
        </button>
        <button type="button" className="drill-exit" onClick={download}>
          Download (.md)
        </button>
        <button type="button" className="drill-exit" onClick={() => setOpen(false)}>
          Close
        </button>
      </div>
    </div>
  )
}
